// @flow
import React from 'react'
import classNames from 'classnames'
import DatetimePicker from 'react-datetime'

import AbstractFormField from './AbstractFormField'

// core
import { parseRelativeDatetime } from '../../DateTimeFunctions'
import moment from '../../Moment'

// types
import type { DefaultFormFieldType, DefaultFormFieldState } from './AbstractFormField'

type DatePickerProps = DefaultFormFieldType & {
  domain: {
    _type: string,
    maxDate: string,
    maxDateRelative: string,
    minDate: string,
    minDateRelative: string,
  },
  value: string,
}

type DatePickerState = DefaultFormFieldState & {
  maxDate: Object | null,
  minDate: Object | null,
  value: Object | string,
}

export default class DatePicker extends AbstractFormField<DatePickerProps, DatePickerState> {

  constructor(props: DatePickerProps) {
    super(props)
    this.state = {
      maxDate: null,
      minDate: null,
      prevPropsValue: '',
      value: '',
      pristine: true,
    }
  }

  componentDidMount() {
    if (this.props.value) {
      this.checkDate(this.props.value)
    }
    if (!this.props.textMode) {
      this.changeRange(this.props)
    }
  }

  componentDidUpdate(prevProps: DatePickerProps) {
    if (this.props.value !== prevProps.value) {
      this.checkDate(this.props.value)
    }
    if (!this.props.textMode && JSON.stringify(this.props.domain) !== JSON.stringify(prevProps.domain)) {
      this.changeRange(this.props)
    }
  }

  checkDate = (date: string) => {
    let newState: Object = { }
    const dateObject = moment(date, ['YYYY-MM-DD', 'DD.MM.YYYY'])
    if (!dateObject.isValid() && date !== '') {
      this.props.onChange('')
    } else {
      newState.value = dateObject
    }
    this.setState(newState)
  }

  changeRange = (props: DatePickerProps) => {
    let minDate = null
    let maxDate = null

    if (props.domain.minDate) {
      minDate = moment(props.domain.minDate).startOf('day')
    } else if (props.domain.minDateRelative) {
      minDate = parseRelativeDatetime(props.domain.minDateRelative).startOf('day')
    }
    if (props.domain.maxDate) {
      maxDate = moment(props.domain.maxDate).endOf('day')
    } else if (props.domain.maxDateRelative) {
      maxDate = parseRelativeDatetime(props.domain.maxDateRelative).endOf('day')
    }

    this.setState({ minDate, maxDate })
  }

  handleIsValidDate = (currentDate: Object) => {
    const minPassed = !this.state.minDate ? true : currentDate.diff(this.state.minDate) > 0
    const maxPassed = !this.state.maxDate ? true : this.state.maxDate.diff(currentDate) > 0
    return minPassed && maxPassed
  }

  handleOnChange = (date: string | Object) => {
    if (!date || typeof date === 'string') {
      this.setState({ value: '', pristine: false })
    } else {
      this.setState({ value: date })
    }
  }

  handleOnBlur = (date: string | Object) => {
    this.setState({ pristine: false })
    let value = ''
    if (!date || typeof date === 'string' || !this.handleIsValidDate(date)) {
      this.props.onChange('')
    } else {
      value = date.format('YYYY-MM-DD')
      this.props.onChange(value)
    }
    if (this.props.onBlur) {
      this.props.onBlur(`${this.props.prefix}${this.props.name}`, value, [], value !== this.props.value)
    }
  }

  render() {
    if (this.props.textMode) {
      return <span>{this.state.value ? this.state.value.format('DD.MM.YYYY') : ''}</span>
    }

    const classes = ['form-control']
    const wrapperClasses = ['react-datepicker']
    if (this.hasErrors() && !this.isPristine()) {
      classes.push('is-invalid')
      wrapperClasses.push('is-invalid')
    }

    return (
      <div className={classNames(wrapperClasses)}>
        <DatetimePicker
          closeOnSelect
          inputProps={{
            className: classNames(classes),
            id: this.props.id,
            name: this.props.name,
            readOnly: !this.props.enabled,
          }}
          isValidDate={this.handleIsValidDate}
          locale='cs-CZ'
          onBlur={this.handleOnBlur}
          onChange={this.handleOnChange}
          strictParsing
          timeFormat={false}
          value={this.state.value}
          viewMode='years'
        />
        {this.renderError()}
      </div>
    )
  }
}
